import React, { memo } from 'react';

import InputSearch, { InputSize } from '@zvuk-b2b/react-uikit/ui/InputSearch';
import IconSettingsSlider from '@zvuk-b2b/react-uikit/ui/icons/Settings copy';

import AppliedTags from './AppliedTags';
import TagFilterDropdown from './TagFilterDropdown';
import type { AddressTagFilterProps } from './types';
import useAddressTagFilterViewModel from './useAddressTagFilterViewModel';

import styles from './index.module.scss';

const AddressTagFilter: React.FC<AddressTagFilterProps> = ({
  isExactSearch,
  isLoading = false,
  isOpen,
  search,
  searchSize = InputSize.MEDIUM,
  selectedTagIds,
  tags,
  texts,
  testIds,
  onExactSearchChange,
  onOpenChange,
  onSearchChange,
  onSelectedTagIdsChange,
}) => {
  const {
    containerRef,
    dropdownRef,
    filterButtonRef,
    filteredTags,
    hiddenSelectedTagsCount,
    isAppliedTagsExpanded,
    isAppliedTagsToggleVisible,
    onAppliedTagsToggle,
    onClear,
    onFilterButtonClick,
    onTagChange,
    onTagRemove,
    selectedTagSet,
    selectedTags,
    setTagSearch,
    tagSearch,
    visibleSelectedTags,
  } = useAddressTagFilterViewModel({
    isExactSearch,
    isLoading,
    isOpen,
    search,
    searchSize,
    selectedTagIds,
    tags,
    texts,
    testIds,
    onExactSearchChange,
    onOpenChange,
    onSearchChange,
    onSelectedTagIdsChange,
  });

  return (
    <div
      ref={containerRef}
      className={styles.container}>
      <div className={styles.searchRow}>
        <InputSearch
          elementId={testIds.mainSearch}
          value={search}
          placeholder={texts.mainSearchPlaceholder}
          size={searchSize}
          isLoading={isLoading}
          containerClassName={styles.mainSearch}
          inputClassName={styles.searchInput}
          onChange={onSearchChange}
        />

        <button
          ref={filterButtonRef}
          type="button"
          className={styles.filterButton}
          aria-label={texts.filterButtonLabel}
          aria-expanded={isOpen}
          data-testid={testIds.filterButton}
          onClick={onFilterButtonClick}>
          <span className={styles.filterButtonIconWrapper}>
            {selectedTagIds.length > 0 && (
              <span
                className={styles.filterButtonBadge}
                data-testid={testIds.filterBadge}
              />
            )}
            <IconSettingsSlider />
          </span>
        </button>
      </div>

      {selectedTags.length > 0 && (
        <>
          <AppliedTags
            hiddenSelectedTagsCount={hiddenSelectedTagsCount}
            isExpanded={isAppliedTagsExpanded}
            isToggleVisible={isAppliedTagsToggleVisible}
            tags={visibleSelectedTags}
            texts={texts}
            testIds={testIds}
            onClear={onClear}
            onRemove={onTagRemove}
            onToggle={onAppliedTagsToggle}
          />
          {testIds.appliedTagsDivider && (
            <div
              className={styles.divider}
              data-testid={testIds.appliedTagsDivider}
            />
          )}
        </>
      )}

      {isOpen && (
        <TagFilterDropdown
          dropdownRef={dropdownRef}
          isExactSearch={isExactSearch}
          selectedTagIds={selectedTagSet}
          tagSearch={tagSearch}
          tags={filteredTags}
          texts={texts}
          testIds={testIds}
          onExactSearchChange={onExactSearchChange}
          onSearchChange={setTagSearch}
          onTagChange={onTagChange}
        />
      )}
    </div>
  );
};

export default memo(AddressTagFilter);
